import {
  createEntityAdapter,
  createSlice,
  EntityId,
  EntityState,
  PayloadAction
} from '@reduxjs/toolkit';
import { CareChannelListProps } from 'contexts/MessagesContext/messagesContext.types';
import { Status } from 'enums/messages';
import type { RootState } from 'store';

import { channelsApiSlice } from './channelsApi';

const channelsAdapter = createEntityAdapter<CareChannelListProps, EntityId>({
  selectId: (channel) => channel.channelId
});

const initialState: {
  currentChannel: CareChannelListProps | null;
  data: EntityState<CareChannelListProps, EntityId>;
  loadingChannelsStatus: Status;
} = {
  currentChannel: null,
  data: channelsAdapter.getInitialState(),
  loadingChannelsStatus: Status.Idle
};

let lastRequestId: string;

const channelsSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(
      channelsApiSlice.endpoints.getPatientChannels.matchFulfilled,
      (state, { meta, payload }) => {
        if (lastRequestId === meta.requestId) {
          const patientId = meta.arg.originalArgs.patientId;
          const existingIds = channelsAdapter.getSelectors().selectIds(state.data);
          const existingPatientId = existingIds.length
            ? state.data.entities[existingIds[0] as EntityId]?.patientId
            : null;
          const shouldReplace = !!existingPatientId && existingPatientId !== patientId;

          // NOTE: If the patientId is different, then we replace the existing channels with the new ones
          // Otherwise, we update the existing channels with cached channels
          if (shouldReplace) {
            channelsAdapter.setAll(state.data, payload);
            // Update currentChannel if channels are replaced
            if (state.currentChannel?.channelId !== payload?.[0]?.channelId) {
              state.currentChannel = payload?.[0] || null;
            }
          } else {
            channelsAdapter.upsertMany(state.data, payload);
          }

          state.loadingChannelsStatus = Status.Fulfilled;
        }
      }
    );
    builder.addMatcher(
      channelsApiSlice.endpoints.getPatientChannels.matchPending,
      (state, action) => {
        lastRequestId = action.meta.requestId;
        state.loadingChannelsStatus = Status.Pending;
      }
    );
    builder.addMatcher(channelsApiSlice.endpoints.getPatientChannels.matchRejected, (state) => {
      state.loadingChannelsStatus = Status.Rejected;
    });
    builder.addMatcher(
      channelsApiSlice.endpoints.getFrontDeskChannels.matchFulfilled,
      (state, { meta, payload }) => {
        const patientId = meta.arg.originalArgs.patientId;
        const existingIds = channelsAdapter.getSelectors().selectIds(state.data);
        const existingPatientId = existingIds.length
          ? state.data.entities[existingIds[0] as EntityId]?.patientId
          : null;
        const shouldReplace = !!existingPatientId && existingPatientId !== patientId;

        // NOTE: If the patientId is different, then we replace the existing channels with the new ones
        // Otherwise, we update the existing channels with cached channels
        if (shouldReplace) {
          channelsAdapter.setAll(state.data, payload);
          // Update currentChannel if channels are replaced
          if (state.currentChannel?.channelId !== payload?.[0]?.channelId) {
            state.currentChannel = payload?.[0] || null;
          }
        } else {
          channelsAdapter.upsertMany(state.data, payload);
        }

        state.loadingChannelsStatus = Status.Fulfilled;
      }
    );
    builder.addMatcher(channelsApiSlice.endpoints.getFrontDeskChannels.matchPending, (state) => {
      state.loadingChannelsStatus = Status.Pending;
    });
    builder.addMatcher(channelsApiSlice.endpoints.getFrontDeskChannels.matchRejected, (state) => {
      state.loadingChannelsStatus = Status.Rejected;
    });
    builder.addMatcher(
      channelsApiSlice.endpoints.createChannel.matchFulfilled,
      (state, { meta, payload }) => {
        const patientId = meta.arg.originalArgs.patientId;

        const channelData: CareChannelListProps = {
          channelDescription: payload.channelDescription,
          channelId: payload._id,
          channelTitle: payload.channelTitle,
          isCompleted: payload.isCompleted,
          isResponded: payload.isResponded,
          latestMessage: payload.latestMessage,
          patientId,
          unreadMessageCount: 0
        };

        channelsAdapter.upsertOne(state.data, channelData);
      }
    );
  },
  initialState,
  name: 'channels',
  reducers: {
    resetChannels: (state) => {
      if (state.loadingChannelsStatus === Status.Pending) {
        return { ...initialState, loadingChannelsStatus: Status.Pending };
      } else {
        return initialState;
      }
    },
    setChannel: (state, action: PayloadAction<CareChannelListProps | null>) => {
      state.currentChannel = action.payload;
    },
    updateChannel: (
      state,
      action: PayloadAction<{
        channelId: string;
        isCompleted?: boolean;
        isResponded?: boolean;
        latestMessage?: string;
        latestMessageDate?: string;
        unreadMessageCount?: number;
      }>
    ) => {
      const selected = channelsAdapter
        .getSelectors()
        .selectById(state.data, action.payload.channelId);

      if (selected) {
        const update = { ...selected, ...action.payload };
        channelsAdapter.upsertOne(state.data, update);

        // Update currentChannel if the updated channel is currently selected
        if (state.currentChannel?.channelId === action.payload.channelId) {
          state.currentChannel = {
            ...state.currentChannel,
            ...action.payload
          };
        }
      }
    }
  }
});

export const { resetChannels, setChannel, updateChannel } = channelsSlice.actions;

export const channelsSelectors = channelsAdapter.getSelectors<RootState>(
  (state) => state.channels.data
);

export const selectChannels = (state: RootState) => state.channels;

export default channelsSlice.reducer;
